import { VStack, Box, Text, useDisclosure, Button, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, FormControl, FormLabel, Input, Spinner, Select } from '@chakra-ui/react';
import { useState } from 'react';
import { BestMatch } from './BestMatch';
import { HighConfidenceMatches } from './HighConfidenceMatches';
import { LowerProbabilityMatches } from './LowerProbabilityMatches';
import { MatchStatsModal } from './MatchStatsModal';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { getDocumentUrl, extractMiscIntelDetails } from '../../utils/helpers';

interface SearchResultsProps {
  searchResults: any[];
  submittedFaceUrl: string | null;
  API_URL: string;
}

export function SearchResults({ searchResults, submittedFaceUrl, API_URL }: SearchResultsProps) {
  const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();
  const { isOpen: isReportModalOpen, onOpen: onReportModalOpen, onClose: onReportModalClose } = useDisclosure();
  const [selectedMatchStats, setSelectedMatchStats] = useState<any>(null);
  const [selectedMatches, setSelectedMatches] = useState<Set<string>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportDetails, setReportDetails] = useState({
    requestingAgency: '',
    preparedBy: '',
    crimeType: '',
    otherCrimeType: ''
  });
  const toast = useToast();

  const crimeTypes = [
    'Assault',
    'Burglary',
    'Narcotics Offenses',
    'Fraud/Identity Theft',
    'Larceny',
    'Robbery',
    'Sexual Assault',
    'Vandalism',
    'Weapons Offenses',
    'Other'
  ];

  const handleMatchSelect = (match: any, selected: boolean) => {
    const newSelected = new Set(selectedMatches);
    if (selected) {
      newSelected.add(match.image_path);
    } else {
      newSelected.delete(match.image_path);
    }
    setSelectedMatches(newSelected);
  };

  const generateReport = async () => {
    if (!reportDetails.requestingAgency || !reportDetails.preparedBy || !reportDetails.crimeType || (reportDetails.crimeType === 'Other' && !reportDetails.otherCrimeType)) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsGenerating(true);
    const toastId = toast({
      title: 'Generating Report',
      description: 'This may take a few moments...',
      status: 'info',
      duration: null,
      isClosable: false,
    });

    try {
      const doc = new jsPDF();
      const selectedMatchesData = searchResults.filter(match => 
        selectedMatches.has(match.image_path)
      );

      // Preload all images in parallel
      const headerImg = new Image();
      headerImg.src = '/images/rtc_header1.png';
      await new Promise((resolve) => {
        headerImg.onload = resolve;
      });

      // Preload query image
      const queryImg = new Image();
      if (submittedFaceUrl) {
        queryImg.src = `${API_URL}/api/faces/${submittedFaceUrl.split('/').pop()}`;
        await new Promise((resolve) => {
          queryImg.onload = resolve;
        });
      }

      // Preload all match images in parallel
      const imagePromises = selectedMatchesData.map(async (match) => {
        const matchImg = new Image();
        matchImg.src = `${API_URL}/api/faces/${match.image_path.split('/').pop()}`;
        await new Promise((resolve) => {
          matchImg.onload = resolve;
        });
        return { match, matchImg };
      });

      const loadedImages = await Promise.all(imagePromises);

      // Helper function to add header to a page
      const addHeaderToPage = () => {
        doc.addImage(headerImg, 'PNG', 0, 0, 210, 60);
        // Add a subtle line under the header
        doc.setDrawColor(200, 200, 200);
        doc.line(20, 65, 190, 65);
      };

      // Helper function to add footer to a page
      const addFooterToPage = () => {
        // Add a subtle line above the footer
        doc.setDrawColor(200, 200, 200);
        doc.line(20, 270, 190, 270);

        // Disclaimer
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.setFont('Bookman Old Style', 'italic');
        const disclaimer = "This match was generated by an automated facial recognition system. It is not confirmation of identity. All potential matches must be verified through investigative follow-up. Do not rely solely on this document to make an arrest or take enforcement action.";
        doc.text(disclaimer, 105, 275, { align: 'center', maxWidth: 170 });
        doc.setFont('Bookman Old Style', 'normal');

        // Contact and Prepared By
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text('RTC Contact: DPS-RTC@westchestercountyny.gov', 20, 285);
        doc.text('Prepared by: ' + reportDetails.preparedBy, 20, 290);
      };

      // Cover Page
      addHeaderToPage();
      
      // Set default font to Bookman Old Style
      doc.setFont('Bookman Old Style');
      
      // Title with decorative line
      doc.setFontSize(24);
      doc.setTextColor(44, 62, 80);
      doc.text('Facial Recognition Match Report', 105, 80, { align: 'center' });
      doc.setDrawColor(44, 62, 80);
      doc.line(60, 85, 150, 85);
      
      // Report Details in a styled box
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(20, 95, 170, 60, 3, 3, 'F');
      
      // Top row
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.setFont('Bookman Old Style', 'bold');
      doc.text('Date/Time', 30, 105);
      doc.text('Requesting Agency', 120, 105);
      doc.setFont('Bookman Old Style', 'normal');
      doc.text(new Date().toLocaleString(), 30, 115);
      doc.text(reportDetails.requestingAgency, 120, 115);

      // Bottom row
      doc.setFont('Bookman Old Style', 'bold');
      doc.text('Prepared By', 30, 135);
      doc.text('Crime Type', 120, 135);
      doc.setFont('Bookman Old Style', 'normal');
      doc.text(reportDetails.preparedBy, 30, 145);
      doc.text(reportDetails.crimeType === 'Other' ? reportDetails.otherCrimeType : reportDetails.crimeType, 120, 145);

      // Disclaimer in a styled box
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(20, 165, 170, 100, 3, 3, 'F');
      
      // Disclaimer header
      doc.setFontSize(20);
      doc.setTextColor(44, 62, 80);
      doc.setFont('Bookman Old Style', 'bolditalic');
      doc.text('RTC InSight Disclaimer', 105, 175, { align: 'center' });
      
      // Disclaimer text
      doc.setFontSize(16);
      doc.setTextColor(100, 100, 100);
      doc.setFont('Bookman Old Style', 'italic');
      const frontPageDisclaimer = [
        "This tool assists in identifying potential suspects but is not always accurate. Results may vary with image quality and conditions and should not be the sole basis for decision-making.",
        "",
        "",
        "Results are intended for investigative leads only and are not definitive identifications. All matches should be verified through additional investigation.",
        "",
        "",
        "Do not use results as the sole basis for any law enforcement actions. Establish probable cause independently of this tool."
      ];
      doc.text(frontPageDisclaimer, 105, 185, { align: 'center', maxWidth: 160 });
      doc.setFont('Bookman Old Style', 'normal');

      // Matches Section
      for (const { match, matchImg } of loadedImages) {
        doc.addPage();
        addHeaderToPage();
        
        // Match Header
        doc.setFontSize(18);
        doc.setTextColor(44, 62, 80);
        const confidenceScore = match.consensus?.original_similarity || 0;
        const confidencePercentage = (confidenceScore * 100).toFixed(1);
        doc.text(`Match ${selectedMatchesData.indexOf(match) + 1} (${confidencePercentage}% Cosine Similarity)`, 105, 85, { align: 'center' });

        // Images side by side with borders
        doc.setDrawColor(200, 200, 200);
        doc.rect(20, 105, 80, 80);
        doc.rect(110, 105, 80, 80);
        doc.addImage(queryImg, 'JPEG', 20, 105, 80, 80);
        doc.addImage(matchImg, 'JPEG', 110, 105, 80, 80);

        // Labels
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Query Image', 20, 190);
        doc.text('Matched Image', 110, 190);

        // Metadata
        doc.setFontSize(12);
        doc.setTextColor(44, 62, 80);
        let yOffset = 200;

        if (match.source === 'misc_intel') {
          const details = extractMiscIntelDetails(match.raw_image_path, match.image_path);
          // Left side
          doc.setFont('Bookman Old Style', 'bold');
          doc.text('Document Name', 30, yOffset, { align: 'left' });
          doc.setFont('Bookman Old Style', 'normal');
          doc.text(details.fileName, 30, yOffset + 10, { align: 'left' });
          
          // Right side
          doc.setFont('Bookman Old Style', 'bold');
          doc.text('Page Number', 120, yOffset, { align: 'left' });
          doc.setFont('Bookman Old Style', 'normal');
          doc.text(details.pageNumber.toString(), 120, yOffset + 10, { align: 'left' });
          
          yOffset += 20;
        } else if (match.details) {
          // Top Left - Name
          doc.setFont('Bookman Old Style', 'bold');
          doc.text('Name', 30, yOffset, { align: 'left' });
          doc.setFont('Bookman Old Style', 'normal');
          doc.text(match.details.name || 'N/A', 30, yOffset + 10, { align: 'left' });
          
          // Top Right - Booking Date
          if (match.details.booking_date) {
            doc.setFont('Bookman Old Style', 'bold');
            doc.text('Booking Date', 120, yOffset, { align: 'left' });
            doc.setFont('Bookman Old Style', 'normal');
            doc.text(match.details.booking_date, 120, yOffset + 10, { align: 'left' });
          }
          
          yOffset += 20;
          
          // Bottom Left - DOB
          doc.setFont('Bookman Old Style', 'bold');
          doc.text('DOB', 30, yOffset, { align: 'left' });
          doc.setFont('Bookman Old Style', 'normal');
          doc.text(match.details.dob || 'N/A', 30, yOffset + 10, { align: 'left' });
          
          // Bottom Right - Top Charge
          if (match.details.top_charge) {
            doc.setFont('Bookman Old Style', 'bold');
            doc.text('Top Charge', 120, yOffset, { align: 'left' });
            doc.setFont('Bookman Old Style', 'normal');
            doc.text(match.details.top_charge, 120, yOffset + 10, { align: 'left' });
          }
          
          yOffset += 20;
        }

        // Source Information
        const sourceLabels: { [key: string]: string } = {
          'rici_database': 'Westchester Bookings',
          'fugitive_database': 'Westchester Fugitive Database',
          'slack_mattermost_export': 'Slack Archive',
          'satg_suspects': 'SATG Suspects',
          'misc_intel': 'Misc Intel Documents',
          'oj_bookings': 'Northern Bookings',
          'rockland_rici': 'Rockland Bookings'
        };
        const sourceLabel = sourceLabels[match.source] || match.source;
        doc.setFont('Bookman Old Style', 'bold');
        doc.text('Source', 105, yOffset + 10, { align: 'center' });
        doc.setFont('Bookman Old Style', 'normal');
        doc.text(sourceLabel, 105, yOffset + 20, { align: 'center' });

        // Add footer to each match page
        addFooterToPage();
      }

      // Save the PDF
      const dateStr = new Date().toISOString().split('T')[0];
      const crimeTypeStr = reportDetails.crimeType === 'Other' 
        ? reportDetails.otherCrimeType.replace(/[^a-z0-9]/gi, '-').toLowerCase()
        : reportDetails.crimeType.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const agencyStr = reportDetails.requestingAgency.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const filename = `facial-recognition-report-${agencyStr}-${crimeTypeStr}-${dateStr}.pdf`;

      // Save PDF to server
      try {
        const pdfBase64 = doc.output('datauristring').split(',')[1];
        const response = await fetch(`${API_URL}/api/save-report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename,
            pdfData: pdfBase64,
            metadata: {
              requestingAgency: reportDetails.requestingAgency,
              preparedBy: reportDetails.preparedBy,
              crimeType: reportDetails.crimeType === 'Other' ? reportDetails.otherCrimeType : reportDetails.crimeType,
              date: dateStr,
              matchCount: selectedMatchesData.length
            }
          }),
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to save report to server');
        }

        // Save to local machine after successful server save
        doc.save(filename);
        onReportModalClose();
        setIsSelectMode(false);
        setSelectedMatches(new Set());
        toast.close(toastId);
        toast({
          title: 'Report Generated',
          description: 'Your report has been saved and downloaded.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error saving report:', error);
        toast.close(toastId);
        toast({
          title: 'Error',
          description: 'Failed to save report to server. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.close(toastId);
      toast({
        title: 'Error',
        description: 'Failed to generate report. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={8} w="full" minH="100vh" overflowY="auto" pb={8}>
      <Box w="full" display="flex" justifyContent="flex-end" px={4}>
        <Button
          colorScheme={isSelectMode ? "blue" : "gray"}
          onClick={() => setIsSelectMode(!isSelectMode)}
          mr={2}
        >
          {isSelectMode ? "Cancel Selection" : "Select Matches"}
        </Button>
        {isSelectMode && (
          <Button
            colorScheme="green"
            onClick={onReportModalOpen}
            isDisabled={selectedMatches.size === 0}
          >
            Generate Report ({selectedMatches.size} selected)
          </Button>
        )}
      </Box>

      {searchResults.length === 0 ? (
        <Box w="full" bg="gray.800" p={6} borderRadius="xl" textAlign="center">
          <Text fontSize="xl" fontWeight="bold" color="white">
            No matches found
          </Text>
        </Box>
      ) : (
        <>
          <BestMatch 
            match={searchResults[0]}
            submittedFaceUrl={submittedFaceUrl}
            API_URL={API_URL}
            onStatsOpen={onStatsOpen}
            setSelectedMatchStats={setSelectedMatchStats}
            isSelectable={isSelectMode}
            isSelected={selectedMatches.has(searchResults[0].image_path)}
            onSelect={handleMatchSelect}
          />

          <HighConfidenceMatches 
            matches={searchResults.slice(1)}
            submittedFaceUrl={submittedFaceUrl}
            API_URL={API_URL}
            onStatsOpen={onStatsOpen}
            setSelectedMatchStats={setSelectedMatchStats}
            isSelectable={isSelectMode}
            selectedMatches={selectedMatches}
            onSelect={handleMatchSelect}
          />

          <LowerProbabilityMatches 
            matches={searchResults}
            submittedFaceUrl={submittedFaceUrl}
            API_URL={API_URL}
            onStatsOpen={onStatsOpen}
            setSelectedMatchStats={setSelectedMatchStats}
            isSelectable={isSelectMode}
            selectedMatches={selectedMatches}
            onSelect={handleMatchSelect}
          />

          <MatchStatsModal 
            isOpen={isStatsOpen}
            onClose={onStatsClose}
            selectedMatchStats={selectedMatchStats}
          />

          <Modal isOpen={isReportModalOpen} onClose={onReportModalClose}>
            <ModalOverlay />
            <ModalContent bg="gray.800" color="white">
              <ModalHeader>Generate Match Report</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <FormControl mb={4}>
                  <FormLabel>Requesting Agency</FormLabel>
                  <Input 
                    value={reportDetails.requestingAgency}
                    onChange={(e) => setReportDetails(prev => ({
                      ...prev,
                      requestingAgency: e.target.value
                    }))}
                    placeholder="Enter requesting agency"
                    isDisabled={isGenerating}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Prepared By</FormLabel>
                  <Input 
                    value={reportDetails.preparedBy}
                    onChange={(e) => setReportDetails(prev => ({
                      ...prev,
                      preparedBy: e.target.value
                    }))}
                    placeholder="Enter your name"
                    isDisabled={isGenerating}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Crime Type</FormLabel>
                  <Select
                    value={reportDetails.crimeType}
                    onChange={(e) => setReportDetails(prev => ({
                      ...prev,
                      crimeType: e.target.value
                    }))}
                    placeholder="Select crime type"
                    isDisabled={isGenerating}
                    bg="gray.700"
                    color="white"
                    _hover={{ bg: "gray.600" }}
                    _focus={{ bg: "gray.600" }}
                  >
                    {crimeTypes.map((type) => (
                      <option key={type} value={type} style={{ background: "#2D3748", color: "white" }}>{type}</option>
                    ))}
                  </Select>
                </FormControl>
                {reportDetails.crimeType === 'Other' && (
                  <FormControl mb={4}>
                    <FormLabel>Specify Crime Type</FormLabel>
                    <Input 
                      value={reportDetails.otherCrimeType}
                      onChange={(e) => setReportDetails(prev => ({
                        ...prev,
                        otherCrimeType: e.target.value
                      }))}
                      placeholder="Enter crime type"
                      isDisabled={isGenerating}
                    />
                  </FormControl>
                )}
                <Button 
                  colorScheme="blue" 
                  mt={4} 
                  onClick={generateReport} 
                  w="full"
                  isLoading={isGenerating}
                  loadingText="Generating Report..."
                  spinner={<Spinner size="sm" mr={2} />}
                  isDisabled={!reportDetails.requestingAgency || !reportDetails.preparedBy || !reportDetails.crimeType || (reportDetails.crimeType === 'Other' && !reportDetails.otherCrimeType)}
                >
                  Generate Report
                </Button>
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      )}
    </VStack>
  );
}